import posthog from '@/lib/posthog';
import { captureException as sentryCaptureException } from '@/lib/observability/sentry';

interface ApiErrorContext {
  tool: string;
  endpoint?: string;
  statusCode?: number;
}

/**
 * Coerce an unknown thrown value into a native Error, preserving the message.
 *
 * Supabase returns PostgrestError / AuthApiError objects that are NOT
 * instanceof Error. Calling String() on them produces "[object Object]",
 * which destroys all diagnostic information. This helper checks for a
 * string `message` property first so those objects are reported correctly.
 * The original object is attached as `cause` so Supabase error codes and
 * details are not lost.
 */
export function toError(err: unknown): Error {
  if (err instanceof Error) return err;
  if (
    err !== null &&
    typeof err === 'object' &&
    'message' in err &&
    typeof (err as Record<string, unknown>).message === 'string'
  ) {
    const msg = (err as Record<string, unknown>).message as string;
    const wrapped = new Error(msg);
    (wrapped as Error & { cause?: unknown }).cause = err;
    return wrapped;
  }
  return new Error(String(err));
}

/**
 * Central error reporter. Sends to PostHog (always active) and Sentry
 * (active once VITE_SENTRY_DSN is configured). Pass `context` for API
 * failures so PostHog records a queryable `tool_api_error` event.
 */
export function reportError(err: unknown, context?: ApiErrorContext): void {
  const error = toError(err);
  posthog.captureException(error);
  sentryCaptureException(error);
  if (context) {
    posthog.capture('tool_api_error', {
      tool: context.tool,
      endpoint: context.endpoint,
      status_code: context.statusCode,
      error_message: error.message,
    });
  }
}
